import { PropsWithChildren, ReactNode, useEffect, useState } from 'react';

import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldValues, useFormContext, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FiltersIcon } from 'assets/svg/sliders-horizontal.svg';
import { BASE_FILTER_FIELDS, ButtonVariants } from 'constants/common';
import { FilterFieldsContainer, Button, Input } from 'shared-components/index';
import { cn } from 'utils/global';

interface Props {
  dataCy?: string;
  children?: ReactNode;
  rightTopActionButton?: ReactNode;
  handleClearFields?: VoidFunction;
  placeHolder: string;
  searchFieldName?: string;
  withFilter?: boolean;
  isSearchFieldName?: boolean;
  openFilter?: boolean;
  isLoading?: boolean;
  alwaysOpenFilters?: boolean;
  inputClassName?: string;
}

const FilterForm = <T extends FieldValues>({
  children,
  dataCy,
  placeHolder,
  rightTopActionButton,
  handleClearFields,
  isSearchFieldName = true,
  searchFieldName,
  withFilter = true,
  isLoading,
  openFilter,
  alwaysOpenFilters,
  inputClassName,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation();

  const [isFiltersOpen, setIsFiltersOpen] = useState(openFilter || false);

  const onClickFiltersButton = () => {
    setIsFiltersOpen(!isFiltersOpen);
  };

  const { register } = useFormContext<T>();

  useEffect(() => {
    if (alwaysOpenFilters) {
      setIsFiltersOpen(true);
    }
  }, [alwaysOpenFilters]);

  return (
    <div data-cy={dataCy}>
      <div className="flex mb-4 justify-between items-start md:items-center flex-col md:flex-row gap-4">
        {isSearchFieldName && (
          <div className={cn('flex flex-col md:flex-row w-full md:w-3/4 items-start md:items-center', inputClassName)}>
            <div className="flex w-full">
              <div className="w-full">
                <Input
                  isLoading={isLoading}
                  {...register((searchFieldName || BASE_FILTER_FIELDS.SEARCH_FIELD) as Path<T>)}
                  type="text"
                  placeholder={placeHolder}
                  inputClassName={cn(
                    '!w-full h-10 text-sm dark:bg-black-900 dark:placeholder:text-neutral-100 dark:hover:border-neutral-200 dark:focus:ring-0 dark:focus:border-neutral-100',
                    {
                      '!rounded-e-none': !isFiltersOpen,
                    }
                  )}
                />
              </div>
              {!isFiltersOpen && (
                <Button dataCy="submit-search-filter" type="submit" className="h-10 w-10 rounded-s-none">
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </Button>
              )}
            </div>

            <div className="w-full flex justify-between items-center">
              {withFilter && (
                <Button
                  dataCy="filter-button"
                  type="button"
                  className="flex md:ml-4 h-10 mt-4 md:mt-0 items-center dark:border-neutral-700"
                  onClick={onClickFiltersButton}
                  variant={ButtonVariants.OUTLINE}
                >
                  <FiltersIcon />
                  <div className="ml-4 ">{t('Filters')}</div>
                </Button>
              )}
              <div className="md:hidden mt-4 flex justify-start">{rightTopActionButton}</div>
            </div>
          </div>
        )}
        <div className="hidden md:flex md:ml-3.5">{rightTopActionButton}</div>
      </div>

      {handleClearFields && (
        <FilterFieldsContainer openFilter={openFilter} handleClearFields={handleClearFields} isOpen={isFiltersOpen}>
          {children}
        </FilterFieldsContainer>
      )}
    </div>
  );
};

export default FilterForm;
